import { useEffect, useId, useState } from "react";
import type { SignInHelpConfirmedResponse, SignInHelpKind } from "@starsach/shared-types";
import { postPublic } from "../api/client";
import { StarsachLockup } from "../components/Brand";
import { ErrorState, btnPrimary, inputClass } from "../components/ui";
import { loadConfig } from "../config";

const SUPPORT = "support@gairm.org";

type Problem = "password" | SignInHelpKind | "sso" | "disabled" | "other";
const PROBLEMS: Array<{ id: Problem; label: string }> = [
  { id: "password", label: "I forgot my password" },
  { id: "authenticator", label: "I lost my authenticator app, or changed phones" },
  { id: "invitation", label: "My invitation expired or never arrived" },
  { id: "sso", label: "My organization signs in with Microsoft, Okta or another provider" },
  { id: "disabled", label: "I was told my access was turned off" },
  { id: "other", label: "Something else" },
];

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  useEffect(() => {
    document.title = `${title} · Starsach`;
  }, [title]);
  return (
    <main className="flex min-h-screen flex-col items-center bg-canvas px-4 py-12 dark:bg-neutral-950">
      <div className="w-full max-w-lg">
        <StarsachLockup className="h-10 w-auto text-ink dark:text-white" />
        <h1 className="mt-6 text-2xl font-semibold text-ink dark:text-white">{title}</h1>
        <div className="mt-4 space-y-3 rounded-xl border border-neutral-200 bg-white p-5 text-sm dark:border-neutral-800 dark:bg-neutral-900">{children}</div>
        <p className="mt-4 text-sm"><a className="text-accent-strong underline dark:text-accent-bright" href="/login">Back to sign-in</a></p>
      </div>
    </main>
  );
}

/**
 * #591: "Trouble signing in?" from the sign-in page. No sign-in, by
 * definition. What the person can fix themselves is said here; what needs a
 * person (a lost authenticator, an invitation) is asked for by address, and
 * goes to their managers and Gairm support only once the link mailed to that
 * address is followed. The answer is the same whether or not the address has
 * an account.
 */
export function SignInHelpPage() {
  const [problem, setProblem] = useState<Problem | undefined>();
  const name = useId();
  return (
    <Shell title="Trouble signing in">
      <fieldset>
        <legend className="font-medium">What is happening?</legend>
        <div className="mt-3 space-y-2">
          {PROBLEMS.map((p) => (
            <label key={p.id} className="flex items-start gap-2">
              <input type="radio" name={name} className="mt-1" checked={problem === p.id} onChange={() => setProblem(p.id)} />
              <span>{p.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {problem && <Answer key={problem} problem={problem} />}
    </Shell>
  );
}

function Answer({ problem }: { problem: Problem }) {
  const box = "mt-4 space-y-3 border-t border-neutral-200 pt-4 dark:border-neutral-800";
  switch (problem) {
    case "password":
      return (
        <div className={box}>
          <p>Choose <strong>Sign in</strong>, enter your email address, then <strong>Forgot your password?</strong> We email you a code to set a new one. Your authenticator app stays as it is.</p>
          <a className={btnPrimary} href="/login">Go to sign-in</a>
        </div>
      );
    case "authenticator":
    case "invitation":
      return <AskForHelp kind={problem} />;
    case "sso":
      return (
        <div className={box}>
          <p>Your organization's identity provider checks your password and second step, not Starsach. Your IT help desk can reset them.</p>
          <p>Once that works again, sign in here with your work email address and you will be taken to your provider.</p>
        </div>
      );
    case "disabled":
      return (
        <div className={box}>
          <p>Only the people who manage Starsach in your organization can turn your access back on. Ask one of them; if you do not know who that is, write to <a className="underline" href={`mailto:${SUPPORT}`}>{SUPPORT}</a> from your work address.</p>
        </div>
      );
    case "other":
      return (
        <div className={box}>
          <p>Write to <a className="underline" href={`mailto:${SUPPORT}?subject=${encodeURIComponent("Trouble signing in to Starsach")}`}>{SUPPORT}</a> from the address you sign in with. Say what you see on screen; a screenshot helps.</p>
        </div>
      );
  }
}

function AskForHelp({ kind }: { kind: SignInHelpKind }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<{ busy: boolean; sent?: string; error?: unknown }>({ busy: false });
  const id = useId();
  const send = async () => {
    setState({ busy: true });
    try {
      const config = await loadConfig();
      await postPublic(config, "/v1/public/sign-in-help", { email, kind });
      setState({ busy: false, sent: email.trim() });
    } catch (error) {
      setState({ busy: false, error });
    }
  };
  if (state.sent) {
    return (
      <div role="status" className="mt-4 space-y-2 border-t border-neutral-200 pt-4 dark:border-neutral-800">
        <p>If <span className="break-words font-medium">{state.sent}</span> has a Starsach account, we have sent it a link. Open it within one hour to send your request.</p>
        <p>Nothing arrived after a few minutes? Check your junk folder, or write to <a className="underline" href={`mailto:${SUPPORT}`}>{SUPPORT}</a>.</p>
      </div>
    );
  }
  return (
    <form
      className="mt-4 space-y-3 border-t border-neutral-200 pt-4 dark:border-neutral-800"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <p>
        {kind === "authenticator"
          ? "A manager in your organization can reset your authenticator. The next time you sign in, you set it up again with a new code."
          : "A manager in your organization can send your invitation again, with a new temporary password."}{" "}
        We email you a link first, to check this is really you.
      </p>
      <label htmlFor={id} className="block font-medium">Email address you sign in with</label>
      <input id={id} type="email" autoComplete="username" inputMode="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} required />
      <button type="submit" className={btnPrimary} disabled={state.busy}>{state.busy ? "Sending…" : "Email me a link"}</button>
      {state.error !== undefined && <ErrorState error={state.error} lead="Couldn’t send it." />}
    </form>
  );
}

/** The token rides in the fragment, which no browser sends to a server; it is read once and taken out of the address bar. */
function takeToken(): string {
  const token = new URLSearchParams(window.location.hash.slice(1)).get("t") ?? "";
  if (token) window.history.replaceState(window.history.state, "", window.location.pathname);
  return token;
}

/** #591: the link from the confirmation email. Opening it changes nothing; the button sends the request. */
export function SignInHelpConfirmPage() {
  const [token] = useState(takeToken);
  const [state, setState] = useState<{ busy: boolean; result?: SignInHelpConfirmedResponse; error?: unknown }>({ busy: false });
  const confirm = async () => {
    setState({ busy: true });
    try {
      const config = await loadConfig();
      setState({ busy: false, result: await postPublic<SignInHelpConfirmedResponse>(config, "/v1/public/sign-in-help/confirm", { token }) });
    } catch (error) {
      setState({ busy: false, error });
    }
  };
  return (
    <Shell title="Send your sign-in request">
      {state.result ? (
        <div role="status" className="space-y-2">
          <p>Sent. Your reference is <span className="font-mono font-medium">{state.result.reference}</span>.</p>
          <p>
            The people who manage Starsach in your organization, and Gairm support, have been told. One of them may contact you to check it is you.{" "}
            {state.result.kind === "authenticator" ? "Once they reset it, sign in as usual and set up your authenticator again." : "Once they send it, the new invitation arrives by email with a temporary password."}
          </p>
          <p>Quote the reference if you write to <a className="underline" href={`mailto:${SUPPORT}`}>{SUPPORT}</a>.</p>
        </div>
      ) : !token ? (
        <p>This link is incomplete. Open it again from the email, or start over from Trouble signing in on the sign-in page.</p>
      ) : (
        <>
          <p>Send your request to the people who can help you sign in again.</p>
          <button type="button" className={btnPrimary} disabled={state.busy} onClick={() => void confirm()}>
            {state.busy ? "Sending…" : "Send my request"}
          </button>
          {state.error !== undefined && <ErrorState error={state.error} lead="Couldn’t send it." />}
        </>
      )}
    </Shell>
  );
}
